import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTime } from "@/context/TimeContext";
import { isManager } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock, Play, Square, Activity, Coffee, Download, Users, CircleDot, FileText, Trash2, AlertTriangle, CalendarDays, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const SECTIONS = [
  { key: "home", label: "Home" }, { key: "matter", label: "Matters" }, { key: "brief-bank", label: "Brief Bank" },
  { key: "practice", label: "Practice Groups" }, { key: "courts", label: "Courts" }, { key: "documents", label: "Documents" },
  { key: "people", label: "People" }, { key: "org", label: "Org Chart" }, { key: "vendors", label: "Vendors" },
  { key: "billing", label: "Billing & Trust" }, { key: "ethics", label: "Ethics & Risk" }, { key: "it", label: "IT & Security" },
  { key: "calendar", label: "Calendar" }, { key: "gallery", label: "Gallery" }, { key: "time", label: "Time Clock" }, { key: "admin", label: "Admin" },
];

const fmtDur = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const fmtClock = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const prodPct = (u) => { const t = (u.active_seconds || 0) + (u.idle_seconds || 0); return t ? Math.round((u.active_seconds / t) * 100) : 0; };

function StatTile({ label, value, sub, tone = "" }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-condensed font-extrabold mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function MyTimecard() {
  const { card, today, recent, elapsed, idleNow, clockedIn, clockIn, clockOut } = useTime();
  return (
    <div data-testid="my-timecard">
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1 border border-border rounded-sm p-6 bg-brand-navy-grad text-white flex flex-col items-center justify-center text-center">
          <Clock className="h-6 w-6 mb-3 opacity-80" />
          {clockedIn ? (
            <>
              <div className="text-4xl font-condensed font-extrabold tabular-nums" data-testid="clock-elapsed">{fmtClock(elapsed)}</div>
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                <span className={`h-2 w-2 rounded-full ${idleNow ? "bg-amber-400" : "bg-emerald-400"}`} />
                {idleNow ? "Idle" : "Active"} · since {fmtTime(card?.clock_in)}
              </div>
              <Button data-testid="clock-out-btn" onClick={clockOut} variant="secondary" className="mt-5 w-full">
                <Square className="h-4 w-4 mr-1.5" /> Clock Out
              </Button>
            </>
          ) : (
            <>
              <div className="text-lg font-semibold">You're clocked out</div>
              <div className="text-xs opacity-80 mt-1">Clock in to start tracking your time.</div>
              <Button data-testid="clock-in-btn" onClick={clockIn} variant="secondary" className="mt-5 w-full">
                <Play className="h-4 w-4 mr-1.5" /> Clock In
              </Button>
            </>
          )}
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <StatTile label="Clocked Today" value={fmtDur(today?.clocked_seconds)} sub={`${today?.shifts || 0} shift(s)`} />
          <StatTile label="Active (Production)" value={fmtDur(today?.active_seconds)} tone="text-emerald-600" />
          <StatTile label="Idle" value={fmtDur(today?.idle_seconds)} tone="text-amber-600" />
          <StatTile label="Productivity" value={`${prodPct(today || {})}%`} sub="active ÷ (active + idle)" />
        </div>
      </div>

      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent Shifts</div>
      {recent?.length ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary/60">
              <TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Clocked</TableHead>
              <TableHead>Active</TableHead><TableHead>Idle</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recent.map((c) => (
                <TableRow key={c.id} data-testid="my-shift-row">
                  <TableCell className="text-sm">{fmtTime(c.clock_in)}</TableCell>
                  <TableCell className="text-sm">{c.clock_out ? fmtTime(c.clock_out) : "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{fmtDur(c.total_seconds)}</TableCell>
                  <TableCell className="text-sm text-emerald-600">{fmtDur(c.active_seconds)}</TableCell>
                  <TableCell className="text-sm text-amber-600">{fmtDur(c.idle_seconds)}</TableCell>
                  <TableCell><Badge variant={c.status === "open" ? "default" : "secondary"} className="text-[10px]">{c.auto_closed ? "auto-closed" : c.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : <EmptyState icon={Clock} title="No shifts yet" hint="Clock in to record your first shift." />}
    </div>
  );
}

function TeamReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const iso = (d) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(() => iso(new Date(Date.now() - 6 * 864e5)));
  const [end, setEnd] = useState(() => iso(new Date()));
  const [dept, setDept] = useState("all");
  const [uid, setUid] = useState("all");
  const [rep, setRep] = useState(null);
  const [live, setLive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saved, setSaved] = useState([]);
  const [onlyNoSignout, setOnlyNoSignout] = useState(false);
  const [tags, setTags] = useState({});
  const [tagDraft, setTagDraft] = useState({});
  const [tagOpen, setTagOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/time/report", { params: { start, end } }).then((r) => setRep(r.data)).catch(() => {});
  }, [start, end]);
  const loadLive = useCallback(() => api.get("/time/live").then((r) => setLive(r.data)).catch(() => {}), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLive(); const t = setInterval(loadLive, 30000); return () => clearInterval(t); }, [loadLive]);
  const loadSaved = useCallback(() => api.get("/time/reports").then((r) => setSaved(r.data)).catch(() => {}), []);
  useEffect(() => { loadSaved(); }, [loadSaved]);
  const loadTags = useCallback(() => api.get("/settings").then((r) => setTags(r.data.section_tags || {})).catch(() => {}), []);
  useEffect(() => { loadTags(); }, [loadTags]);

  const departments = useMemo(() => ["all", ...Array.from(new Set((rep?.users || []).map((u) => u.department).filter(Boolean))).sort()], [rep]);
  const usersList = useMemo(() => rep?.users || [], [rep]);
  const rows = useMemo(() => usersList.filter((u) => (dept === "all" || u.department === dept) && (uid === "all" || u.user_id === uid) && (!onlyNoSignout || (u.auto_closed_shifts || 0) > 0)), [usersList, dept, uid, onlyNoSignout]);

  const totals = useMemo(() => rows.reduce((a, u) => ({
    shifts: a.shifts + u.shifts, clocked: a.clocked + u.clocked_seconds, active: a.active + u.active_seconds,
    idle: a.idle + u.idle_seconds, productive: a.productive + (u.productive_seconds || 0), autoClosed: a.autoClosed + (u.auto_closed_shifts || 0),
  }), { shifts: 0, clocked: 0, active: 0, idle: 0, productive: 0, autoClosed: 0 }), [rows]);

  const openReport = (fmt) => {
    const p = new URLSearchParams({ start, end });
    if (dept !== "all") p.set("department", dept);
    if (uid !== "all") p.set("user_id", uid);
    const path = fmt === "pdf" ? "report/pdf" : fmt === "html" ? "report/html" : "report/export";
    window.open(`${BACKEND}/api/time/${path}?${p.toString()}`, "_blank");
  };
  const saveSnapshot = async () => {
    try { await api.post("/time/reports", { start, end }); toast.success("Report saved to the portal"); loadSaved(); }
    catch { toast.error("Could not save report"); }
  };
  const openSnap = (id, fmt) => window.open(`${BACKEND}/api/time/reports/${id}/${fmt}`, "_blank");
  const delSnap = async (id) => { try { await api.delete(`/time/reports/${id}`); loadSaved(); } catch (e) { console.error("delete report", e); toast.error("Could not delete report"); } };
  const saveWeekly = async () => { const e = new Date(); const s = new Date(Date.now() - 6 * 864e5); try { await api.post("/time/reports", { start: iso(s), end: iso(e), label: "Weekly" }); toast.success("Weekly report saved"); loadSaved(); } catch { toast.error("Could not save"); } };
  const saveMonthly = async () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth(), 1); try { await api.post("/time/reports", { start: iso(s), end: iso(n), label: "Monthly" }); toast.success("Monthly report saved"); loadSaved(); } catch { toast.error("Could not save"); } };
  const saveTags = async (draft) => { try { await api.put("/settings/section_tags", { value: draft }); setTags(draft); setTagOpen(false); toast.success("Section tags saved"); load(); } catch { toast.error("Could not save tags"); } };

  return (
    <div data-testid="team-reports">
      {/* Live now */}
      <div className="border border-border rounded-sm p-4 mb-6" data-testid="live-panel">
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
          <span className="text-sm font-semibold">Clocked In Now</span>
          <span className="ml-auto text-2xl font-condensed font-extrabold" data-testid="live-count">{live?.count || 0}</span>
        </div>
        {live?.cards?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {live.cards.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2" data-testid="live-user">
                <span className={`h-2 w-2 rounded-full shrink-0 ${c.stale ? "bg-amber-400" : "bg-emerald-500"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{c.user_name}</span>
                  <span className="block text-xs text-muted-foreground truncate">{c.current_page || "—"} · {fmtDur(c.total_seconds)}</span>
                </span>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground">No one is clocked in right now.</p>}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><Label className="text-xs">From</Label><Input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" /></div>
        <div>
          <Label className="text-xs">Department</Label>
          <Select value={dept} onValueChange={setDept}><SelectTrigger className="w-52" data-testid="report-dept"><SelectValue /></SelectTrigger>
            <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d === "all" ? "All departments" : d}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-xs">Employee</Label>
          <Select value={uid} onValueChange={setUid}><SelectTrigger className="w-52" data-testid="report-user"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All employees</SelectItem>{usersList.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.user_name}</SelectItem>)}</SelectContent></Select>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none pb-2" data-testid="filter-no-signout">
          <Checkbox checked={onlyNoSignout} onCheckedChange={(v) => setOnlyNoSignout(!!v)} /> No sign-out only
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isAdmin && <Button variant="ghost" size="sm" onClick={() => { setTagDraft({ ...tags }); setTagOpen(true); }} data-testid="section-tags-btn"><SlidersHorizontal className="h-4 w-4 mr-1.5" /> Section Tags</Button>}
          <Button size="sm" onClick={saveSnapshot} data-testid="save-report-btn"><FileText className="h-4 w-4 mr-1.5" /> Save</Button>
          <Button variant="outline" size="sm" onClick={saveWeekly} data-testid="save-weekly-btn"><CalendarDays className="h-4 w-4 mr-1.5" /> Weekly</Button>
          <Button variant="outline" size="sm" onClick={saveMonthly} data-testid="save-monthly-btn"><CalendarDays className="h-4 w-4 mr-1.5" /> Monthly</Button>
          <Button variant="outline" size="sm" onClick={() => openReport("html")} data-testid="view-report-btn"><FileText className="h-4 w-4 mr-1.5" /> View</Button>
          <Button variant="outline" size="sm" onClick={() => openReport("pdf")} data-testid="export-pdf-btn"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={() => openReport("csv")} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatTile label="Total Clocked" value={fmtDur(totals.clocked)} sub={`${totals.shifts} shifts · ${rows.length} people`} />
        <StatTile label="Active" value={fmtDur(totals.active)} tone="text-emerald-600" />
        <StatTile label="On-task (Productive)" value={fmtDur(totals.productive)} tone="text-emerald-700" sub="active on tagged sections" />
        <StatTile label="Idle" value={fmtDur(totals.idle)} tone="text-amber-600" />
        <StatTile label="Productivity" value={`${(totals.active + totals.idle) ? Math.round((totals.active / (totals.active + totals.idle)) * 100) : 0}%`} sub={totals.autoClosed ? `${totals.autoClosed} no sign-out` : ""} />
      </div>

      {/* Per-user table */}
      {rows.length ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary/60">
              <TableHead>Employee</TableHead><TableHead>Dept</TableHead><TableHead>Shifts</TableHead>
              <TableHead>Clocked</TableHead><TableHead>Active</TableHead><TableHead>Idle</TableHead>
              <TableHead>Prod.</TableHead><TableHead>Avg Shift</TableHead><TableHead>Last Out</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.user_id} data-testid="report-row" className="cursor-pointer hover:bg-accent/40" onClick={() => setDetail(u)}>
                  <TableCell className="text-sm font-medium">{u.user_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.department || "—"}</TableCell>
                  <TableCell className="text-sm">{u.shifts}</TableCell>
                  <TableCell className="text-sm font-medium">{fmtDur(u.clocked_seconds)}</TableCell>
                  <TableCell className="text-sm text-emerald-600">{fmtDur(u.active_seconds)}</TableCell>
                  <TableCell className="text-sm text-amber-600">{fmtDur(u.idle_seconds)}</TableCell>
                  <TableCell className="w-28"><div className="flex items-center gap-2"><Progress value={prodPct(u)} className="h-1.5 flex-1" /><span className="text-xs">{prodPct(u)}%</span></div></TableCell>
                  <TableCell className="text-sm">{fmtDur(u.avg_shift_seconds)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtTime(u.last_out)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : <EmptyState icon={Users} title="No time recorded" hint="No shifts in the selected range/filters." />}

      <div className="mt-8" data-testid="saved-reports">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Saved Reports</span>
          <span className="text-xs text-muted-foreground">Auto-generated daily · download anytime</span>
        </div>
        {saved.length ? (
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-secondary/60">
                <TableHead>Report</TableHead><TableHead>Range</TableHead><TableHead>Generated</TableHead>
                <TableHead>Clocked</TableHead><TableHead>Prod.</TableHead><TableHead className="text-right">Download</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {saved.map((s) => (
                  <TableRow key={s.id} data-testid="saved-report-row">
                    <TableCell><Badge variant={String(s.label).includes("auto") ? "secondary" : "default"} className="text-[10px]">{s.label}</Badge></TableCell>
                    <TableCell className="text-sm">{s.start === s.end ? s.start : `${s.start} → ${s.end}`}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtTime(s.generated_at)}</TableCell>
                    <TableCell className="text-sm font-medium">{fmtDur(s.totals?.clocked_seconds)}</TableCell>
                    <TableCell className="text-sm">{prodPct({ active_seconds: s.totals?.active_seconds, idle_seconds: s.totals?.idle_seconds })}%</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openSnap(s.id, "pdf")} data-testid="saved-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                      <Button size="sm" variant="ghost" onClick={() => openSnap(s.id, "csv")} data-testid="saved-csv-btn"><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
                      <button onClick={() => delSnap(s.id)} className="text-muted-foreground hover:text-destructive ml-1 align-middle" data-testid="saved-delete-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : <EmptyState icon={FileText} title="No saved reports yet" hint="A report is saved automatically each day. Use 'Save Report' to capture the current view now." />}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{detail?.user_name}</DialogTitle>
            <DialogDescription>{detail?.department} · {fmtDur(detail?.clocked_seconds)} clocked · {prodPct(detail || {})}% productive</DialogDescription></DialogHeader>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Top Pages / Activity</div>
          {detail?.top_pages?.length ? (
            <div className="space-y-1.5" data-testid="detail-pages">
              {detail.top_pages.map((p) => (
                <div key={p.page} className="flex items-center gap-2 text-sm">
                  <CircleDot className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="flex-1 truncate font-mono text-xs">{p.page || "/"}</span>
                  <span className="text-muted-foreground">{fmtDur(p.seconds)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No page activity recorded.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Productive Section Tags</DialogTitle>
            <DialogDescription>Choose which areas of the hub count as productive time. Time on unchecked sections is excluded from "On-task (Productive)".</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto py-1" data-testid="section-tags-list">
            {SECTIONS.map((s) => {
              const productive = (tagDraft[s.key] ?? "productive") === "productive";
              return (
                <label key={s.key} className="flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2 cursor-pointer select-none" data-testid={`section-tag-${s.key}`}>
                  <Checkbox checked={productive} onCheckedChange={(v) => setTagDraft((d) => ({ ...d, [s.key]: v ? "productive" : "unproductive" }))} />
                  <span className="truncate">{s.label}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagOpen(false)} data-testid="section-tags-cancel">Cancel</Button>
            <Button onClick={() => saveTags(tagDraft)} data-testid="section-tags-save">Save Tags</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TimeClock() {
  const { user } = useAuth();
  const mgr = isManager(user?.role);
  return (
    <div data-testid="time-page">
      <PageHeader overline="Operations" title="Time Clock" subtitle="Clock in and out, and track working vs. idle time. All activity is logged for the firm." />
      {mgr ? (
        <Tabs defaultValue="mine">
          <TabsList className="mb-6">
            <TabsTrigger value="mine" data-testid="tab-my-time"><Clock className="h-4 w-4 mr-1.5" /> My Timecard</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team"><Activity className="h-4 w-4 mr-1.5" /> Team Reports</TabsTrigger>
          </TabsList>
          <TabsContent value="mine"><MyTimecard /></TabsContent>
          <TabsContent value="team"><TeamReports /></TabsContent>
        </Tabs>
      ) : <MyTimecard />}
    </div>
  );
}
